import { useEffect, useState } from "react";
import { X, ChevronLeft } from "lucide-react";
import { usePackageBooking } from "../../../context/PackageBookingContext";
import { useAuth } from "../../../context/AuthContext";
import LoginModal from "../../auth/LoginModal";
import PackageReviewStep from "./Packagereviewstep";
import PackageAddressStep from "./Packageaddressstep";
import PackageSlotStep from "./PackageSlotStep";
import PackageConfirmStep from "./Packageconfirmstep";
import PackageSuccessStep from "./Packagesuccessstep";


const STEPS = {
  REVIEW: "review",
  ADDRESS: "address",
  SLOT: "slot",
  CONFIRM: "confirm",
  SUCCESS: "success",
};

const STEP_ORDER = [
  STEPS.REVIEW,
  STEPS.ADDRESS,
  STEPS.SLOT,
  STEPS.CONFIRM,
  STEPS.SUCCESS,
];

const STEP_META = {
  [STEPS.REVIEW]: { title: "Package Details", subtitle: "Review your package" },
  [STEPS.ADDRESS]: { title: "Service Location", subtitle: "Where should we come?" },
  [STEPS.SLOT]: { title: "Select Slot", subtitle: "Choose date & time" },
  [STEPS.CONFIRM]: { title: "Confirm Booking", subtitle: "Pay after service completion" },
  [STEPS.SUCCESS]: { title: "Booking Confirmed!", subtitle: "You're all set" },
};

const PackageBookingModal = () => {
  const {
    bookingModalOpen,
    closeBookingModal,
    selectedPackage,
    bookingData,
    setBookingData,
    submitting,
    submitPackageBooking,
  } = usePackageBooking();

  const { isAuthenticated, user } = useAuth();
  const [step, setStep] = useState(STEPS.REVIEW);
  const [showLogin, setShowLogin] = useState(false);
  const [bookingDone, setBookingDone] = useState(false);

  // Reset step when modal opens
  useEffect(() => {
    if (bookingModalOpen) {
      setStep(STEPS.REVIEW);
      setBookingDone(false);
    }
  }, [bookingModalOpen]);

  // Sync userId
  useEffect(() => {
    if (user) {
      setBookingData((prev) => ({ ...prev, userId: user.id || user._id }));
    }
  }, [user, setBookingData]);

  // Prevent body scroll
  useEffect(() => {
    if (bookingModalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [bookingModalOpen]);

  if (!bookingModalOpen || !selectedPackage) return null;

  const currentIndex = STEP_ORDER.indexOf(step);

  const goBack = () => {
    if (currentIndex > 0) {
      setStep(STEP_ORDER[currentIndex - 1]);
    }
  };

  const handleProceed = () => {
    if (!isAuthenticated && step === STEPS.REVIEW) {
      setShowLogin(true);
      return;
    }
    const next = STEP_ORDER[currentIndex + 1];
    if (next) setStep(next);
  };

  const isCTADisabled = () => {
    if (step === STEPS.ADDRESS) return !bookingData.__addressValid;
    if (step === STEPS.SLOT) return !bookingData.__slotValid;
    return false;
  };

  const getCTALabel = () => {
    if (!isAuthenticated) return "Login to Book";
    switch (step) {
      case STEPS.REVIEW: return "Select Address";
      case STEPS.ADDRESS: return "Select Slot";
      case STEPS.SLOT: return "Proceed to Confirm";
      default: return "Continue";
    }
  };

  const handleConfirmBooking = async () => {
    const result = await submitPackageBooking({
      ...bookingData,
      packageId: selectedPackage._id,
    });
    if (result.success) {
      setBookingDone(true);
      setStep(STEPS.SUCCESS);
    }
  };

  const showHeader = step !== STEPS.SUCCESS;
  const showFooterCTA = step === STEPS.REVIEW || step === STEPS.ADDRESS || step === STEPS.SLOT;

  return (
    <>
      {/* BACKDROP */}
      <div
        className={`fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 `}
        onClick={() => { if (step !== STEPS.SUCCESS) closeBookingModal(); }}
      >
        {/* MODAL */}
        <div
          onClick={(e) => e.stopPropagation()}
          className={`
            relative bg-white
            w-full sm:max-w-xl
            rounded-t-3xl sm:rounded-2xl
            max-h-[92vh] flex flex-col
            shadow-2xl
            overflow-hidden
            transform  ${bookingModalOpen ? "animate-slideUp" : "animate-slideDown"}
          `}
        >
          {/* HEADER */}
          {showHeader && (
            <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 flex-shrink-0">
              {/* Back button */}
              {currentIndex > 0 && step !== STEPS.SUCCESS && (
                <button
                  onClick={goBack}
                  className="p-2 rounded-xl bg-gray-100 hover:bg-gray-200 transition text-gray-600"
                >
                  <ChevronLeft size={18} />
                </button>
              )}

              {/* Titles */}
              <div className="flex-1 min-w-0">
                <h2 className="text-base font-bold text-gray-900 leading-tight truncate">
                  {STEP_META[step].title}
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  {STEP_META[step].subtitle}
                </p>
              </div>

              {/* Step indicator */}
              {step !== STEPS.SUCCESS && (
                <div className="flex items-center gap-1 flex-shrink-0">
                  {STEP_ORDER.filter(s => s !== STEPS.SUCCESS).map((s, i) => (
                    <div
                      key={s}
                      className={`h-1.5 rounded-full transition-all duration-300 ${s === step
                        ? "w-6 bg-[#ab310c]"
                        : i < currentIndex
                          ? "w-2 bg-[#ab310c]/40"
                          : "w-2 bg-gray-200"
                        }`}
                    />
                  ))}
                </div>
              )}

              {/* Close */}
              {step !== STEPS.SUCCESS && (
                <button
                  onClick={closeBookingModal}
                  className="p-2 rounded-xl bg-gray-100 hover:bg-gray-200 transition text-gray-500 flex-shrink-0"
                >
                  <X size={18} />
                </button>
              )}
            </div>
          )}

          {/* CONTENT */}
          <div className="flex-1 overflow-y-auto no-scrollbar">
            {step === STEPS.REVIEW && (
              <PackageReviewStep pkg={selectedPackage} />
            )}
            {step === STEPS.ADDRESS && (
              <PackageAddressStep
                bookingData={bookingData}
                setBookingData={setBookingData}
              />
            )}
            {step === STEPS.SLOT && (
              <PackageSlotStep
                bookingData={bookingData}
                setBookingData={setBookingData}
              />
            )}
            {step === STEPS.CONFIRM && (
              <PackageConfirmStep
                pkg={selectedPackage}
                bookingData={bookingData}
                submitting={submitting}
                onConfirm={handleConfirmBooking}
                onBack={goBack}
              />
            )}
            {step === STEPS.SUCCESS && (
              <PackageSuccessStep
                pkg={selectedPackage}
                onClose={closeBookingModal}
              />
            )}
          </div>

          {/* FOOTER CTA */}
          {showFooterCTA && (
            <div className="flex-shrink-0 px-5 py-4 border-t border-gray-100 bg-white">
              <button
                onClick={handleProceed}
                disabled={isCTADisabled()}
                className="
                  w-full py-3.5 rounded-xl font-semibold text-white
                  bg-gradient-to-r from-[#ab310c] to-[#d44a20]
                  hover:from-[#d44a20] hover:to-[#ab310c]
                  shadow-md shadow-[#ab310c]/25
                  hover:shadow-lg hover:shadow-[#ab310c]/35
                  transition-all duration-200 active:scale-[0.98]
                  disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none
                "
              >
                {getCTALabel()}
              </button>
            </div>
          )}
        </div>
      </div>

      <LoginModal
        isOpen={showLogin}
        onClose={() => setShowLogin(false)}
        onSuccess={() => {
          setShowLogin(false);
          const next = STEP_ORDER[currentIndex + 1];
          if (next) setStep(next);
        }}
      />
    </>
  );
};

export default PackageBookingModal;